/* ps-motion.js — Page Studio shared storefront effects.
 *
 * Progressive enhancement: pages render and work identically without this
 * file. Sections opt in by emitting data attributes; the ONE guarded
 * deferred <script> tag per opted-in section all resolve to this asset
 * (browser fetches once; the window flag stops double execution).
 *
 * Effects:
 *   [data-ps-effect="<name>"]  — click effect on buttons. Names:
 *       particles: confetti · hearts · sparkles · petals · emoji
 *                  (emoji reads data-ps-effect-emoji, default 🎉)
 *       button:    ripple · pulse · squish · shimmer
 *       feedback:  success (label morphs to ✓, then continues)
 *       big:       rain (confetti rain across the whole viewport)
 *   [data-ps-confetti]         — legacy alias for data-ps-effect="confetti"
 *   [data-ps-scrollscene]      — pinned scroll-scrubbed scene switching
 *
 * Everything respects prefers-reduced-motion.
 */
(function () {
  'use strict';
  if (window.__psMotion) return;
  window.__psMotion = true;

  var reduced = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ─────────────────────── Particle engine ─────────────────────── */

  var BRAND = ['#E2F27F', '#DEE268', '#FEFAF4', '#292F2F'];
  var PINKS = ['#F2A6B8', '#E88AA4', '#E2F27F', '#FEFAF4'];
  var canvas = null;
  var ctx = null;
  var particles = [];
  var rafId = 0;

  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483000;';
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function teardownCanvas() {
    if (!canvas) return;
    canvas.parentNode && canvas.parentNode.removeChild(canvas);
    canvas = null;
    ctx = null;
  }

  function drawHeart(c, s) {
    var r = s * 0.32;
    c.beginPath();
    c.arc(-r, -r * 0.4, r, 0, Math.PI * 2);
    c.arc(r, -r * 0.4, r, 0, Math.PI * 2);
    c.moveTo(-r * 2, -r * 0.1);
    c.lineTo(0, s * 0.62);
    c.lineTo(r * 2, -r * 0.1);
    c.closePath();
    c.fill();
  }

  function drawStar(c, s) {
    c.beginPath();
    for (var i = 0; i < 10; i++) {
      var rad = i % 2 === 0 ? s * 0.55 : s * 0.2;
      var a = (i * Math.PI) / 5 - Math.PI / 2;
      c[i ? 'lineTo' : 'moveTo'](Math.cos(a) * rad, Math.sin(a) * rad);
    }
    c.closePath();
    c.fill();
  }

  function tick() {
    rafId = 0;
    if (!ctx) return;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    var alive = [];
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      if (p.delay > 0) {
        p.delay--;
        alive.push(p);
        continue;
      }
      p.age++;
      p.vy += p.gravity;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx + (p.sway ? Math.sin(p.age * 0.09 + p.seed) * p.sway : 0);
      p.y += p.vy;
      p.rot += p.vr;
      p.life -= 1;
      if (p.life <= 0 || p.y > window.innerHeight + 30) continue;
      alive.push(p);
      var fade = p.life < 25 ? p.life / 25 : 1;
      if (p.twinkle) fade *= 0.55 + 0.45 * Math.sin(p.age * 0.45 + p.seed);
      ctx.globalAlpha = Math.max(0, fade);
      ctx.fillStyle = p.color;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 'heart') {
        drawHeart(ctx, p.size);
      } else if (p.shape === 'star') {
        drawStar(ctx, p.size);
      } else if (p.shape === 'petal') {
        ctx.beginPath();
        ctx.ellipse(0, 0, p.size * 0.55, p.size * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 'glyph') {
        ctx.font = p.size + 'px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.glyph, 0, 0);
      } else {
        /* rect — wobble makes flat rects read as fluttering paper */
        var w = p.size * Math.abs(Math.sin(p.rot * 1.4 + p.seed));
        ctx.fillRect(-p.size / 2, -w / 2, p.size, Math.max(w, 1.5));
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    particles = alive;
    if (particles.length) rafId = requestAnimationFrame(tick);
    else teardownCanvas();
  }

  function emit(list) {
    ensureCanvas();
    for (var i = 0; i < list.length; i++) particles.push(list[i]);
    if (!rafId) rafId = requestAnimationFrame(tick);
  }

  function rand(a, b) {
    return a + Math.random() * (b - a);
  }
  function pick(arr) {
    return arr[(Math.random() * arr.length) | 0];
  }

  function base(x, y) {
    return {
      x: x, y: y, vx: 0, vy: 0, rot: rand(0, Math.PI * 2), vr: rand(-0.15, 0.15),
      size: 8, color: '#E2F27F', shape: 'rect', life: 80, age: 0, delay: 0,
      seed: rand(0, 6), gravity: 0.22, drag: 0.985, sway: 0, twinkle: false, glyph: '',
    };
  }

  function centerOf(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function buttonPalette(el) {
    /* pages carry their own button colors — let the burst pick them up */
    var colors = BRAND.slice();
    try {
      var bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') colors.push(bg, bg);
    } catch (e) { /* ignore */ }
    return colors;
  }

  /* ── particle effect spawners ── */

  function fxConfetti(el) {
    var o = centerOf(el);
    var colors = buttonPalette(el);
    var out = [];
    for (var i = 0; i < 90; i++) {
      var p = base(o.x, o.y);
      var angle = -Math.PI / 2 + rand(-0.75, 0.75);
      var speed = rand(5, 14);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.size = rand(5, 11);
      p.color = pick(colors);
      p.shape = Math.random() < 0.25 ? 'circle' : 'rect';
      p.life = rand(70, 120);
      out.push(p);
    }
    emit(out);
  }

  function fxHearts(el) {
    var o = centerOf(el);
    var out = [];
    for (var i = 0; i < 15; i++) {
      var p = base(o.x + rand(-14, 14), o.y);
      p.vx = rand(-1.2, 1.2);
      p.vy = rand(-4.6, -2);
      p.gravity = -0.015; /* hearts drift UP */
      p.drag = 0.99;
      p.sway = rand(0.5, 1.4);
      p.rot = rand(-0.35, 0.35);
      p.vr = rand(-0.02, 0.02);
      p.size = rand(9, 17);
      p.color = pick(PINKS);
      p.shape = 'heart';
      p.life = rand(70, 110);
      p.delay = (Math.random() * 14) | 0;
      out.push(p);
    }
    emit(out);
  }

  function fxSparkles(el) {
    var o = centerOf(el);
    var out = [];
    for (var i = 0; i < 18; i++) {
      var p = base(o.x, o.y);
      var a = rand(0, Math.PI * 2);
      var speed = rand(1.5, 7.5);
      p.vx = Math.cos(a) * speed;
      p.vy = Math.sin(a) * speed;
      p.gravity = 0;
      p.drag = 0.93;
      p.size = rand(7, 15);
      p.color = pick(['#E2F27F', '#DEE268', '#FFFFFF', '#FEFAF4']);
      p.shape = 'star';
      p.twinkle = true;
      p.vr = rand(-0.08, 0.08);
      p.life = rand(40, 70);
      out.push(p);
    }
    emit(out);
  }

  function fxPetals(el) {
    var o = centerOf(el);
    var out = [];
    for (var i = 0; i < 16; i++) {
      var p = base(o.x, o.y);
      var angle = -Math.PI / 2 + rand(-1.1, 1.1);
      var speed = rand(3, 7.5);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.gravity = 0.055;
      p.drag = 0.985;
      p.sway = rand(0.8, 1.8);
      p.vr = rand(-0.06, 0.06);
      p.size = rand(10, 18);
      p.color = pick(['#E2F27F', '#DEE268', '#F2A6B8', '#FEFAF4']);
      p.shape = 'petal';
      p.life = rand(90, 140);
      out.push(p);
    }
    emit(out);
  }

  function fxEmoji(el) {
    var o = centerOf(el);
    var glyph = el.getAttribute('data-ps-effect-emoji') || '🎉';
    var out = [];
    for (var i = 0; i < 16; i++) {
      var p = base(o.x, o.y);
      var angle = -Math.PI / 2 + rand(-0.8, 0.8);
      var speed = rand(4, 11);
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.size = rand(13, 22);
      p.shape = 'glyph';
      p.glyph = glyph;
      p.vr = rand(-0.1, 0.1);
      p.life = rand(65, 105);
      out.push(p);
    }
    emit(out);
  }

  function fxRain(el) {
    var colors = buttonPalette(el);
    var w = window.innerWidth;
    var out = [];
    for (var i = 0; i < 110; i++) {
      var p = base(rand(0, w), rand(-60, -10));
      p.vx = rand(-0.8, 0.8);
      p.vy = rand(2, 5);
      p.gravity = 0.07;
      p.drag = 0.998;
      p.sway = rand(0, 0.8);
      p.size = rand(5, 11);
      p.color = pick(colors);
      p.shape = Math.random() < 0.3 ? 'circle' : 'rect';
      p.life = 200;
      p.delay = (Math.random() * 45) | 0;
      out.push(p);
    }
    emit(out);
  }

  /* ─────────────────── Button CSS effects ──────────────────── */

  var cssReady = false;
  function ensureFxCss() {
    if (cssReady) return;
    cssReady = true;
    var s = document.createElement('style');
    s.textContent =
      '@keyframes ps-fx-squish{0%{transform:scale(1)}30%{transform:scale(.9,.86)}65%{transform:scale(1.05,1.03)}100%{transform:scale(1)}}' +
      '.ps-fx-squish{animation:ps-fx-squish .45s cubic-bezier(.34,1.56,.64,1)}' +
      '@keyframes ps-fx-pulse{0%{box-shadow:0 0 0 0 rgba(226,242,127,.95)}100%{box-shadow:0 0 0 24px rgba(226,242,127,0)}}' +
      '.ps-fx-pulse{animation:ps-fx-pulse .65s ease-out}' +
      '@keyframes ps-fx-ripple{to{transform:scale(1);opacity:0}}' +
      '.ps-fx-ripple-ink{position:absolute;border-radius:50%;background:rgba(255,255,255,.6);transform:scale(0);animation:ps-fx-ripple .55s ease-out forwards;pointer-events:none}' +
      '@keyframes ps-fx-shimmer{from{left:-45%}to{left:115%}}' +
      '.ps-fx-shimmer-bar{position:absolute;top:0;bottom:0;width:40%;transform:skewX(-16deg);background:linear-gradient(105deg,transparent,rgba(255,255,255,.8),transparent);animation:ps-fx-shimmer .6s ease-out forwards;pointer-events:none}';
    document.head.appendChild(s);
  }

  function hostify(el) {
    /* ripple/shimmer overlays need a positioned, clipping host */
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    el.style.overflow = 'hidden';
  }

  function replayClass(el, cls, ms) {
    el.classList.remove(cls);
    void el.offsetWidth; /* restart the animation */
    el.classList.add(cls);
    setTimeout(function () { el.classList.remove(cls); }, ms);
  }

  function fxSquish(el) {
    ensureFxCss();
    replayClass(el, 'ps-fx-squish', 500);
  }

  function fxPulse(el) {
    ensureFxCss();
    replayClass(el, 'ps-fx-pulse', 700);
  }

  function fxRipple(el, ev) {
    ensureFxCss();
    hostify(el);
    var r = el.getBoundingClientRect();
    var cx = (ev && ev.clientX ? ev.clientX : r.left + r.width / 2) - r.left;
    var cy = (ev && ev.clientY ? ev.clientY : r.top + r.height / 2) - r.top;
    var radius = Math.max(
      Math.hypot(cx, cy), Math.hypot(r.width - cx, cy),
      Math.hypot(cx, r.height - cy), Math.hypot(r.width - cx, r.height - cy));
    var ink = document.createElement('span');
    ink.className = 'ps-fx-ripple-ink';
    ink.style.width = ink.style.height = radius * 2 + 'px';
    ink.style.left = cx - radius + 'px';
    ink.style.top = cy - radius + 'px';
    el.appendChild(ink);
    setTimeout(function () { ink.parentNode && ink.parentNode.removeChild(ink); }, 650);
  }

  function fxShimmer(el) {
    ensureFxCss();
    hostify(el);
    var bar = document.createElement('span');
    bar.className = 'ps-fx-shimmer-bar';
    el.appendChild(bar);
    setTimeout(function () { bar.parentNode && bar.parentNode.removeChild(bar); }, 700);
  }

  function fxSuccess(el) {
    ensureFxCss();
    if (el.__psFxBusy) return;
    el.__psFxBusy = true;
    var orig = el.innerHTML;
    el.style.minWidth = el.offsetWidth + 'px';
    el.textContent = '✓';
    replayClass(el, 'ps-fx-squish', 500);
    setTimeout(function () {
      /* only matters when no navigation happened (e.g. # links) */
      el.innerHTML = orig;
      el.style.minWidth = '';
      el.__psFxBusy = false;
    }, 1200);
  }

  /* ── dispatch ── */

  var FX = {
    confetti: { run: fxConfetti, delay: 600 },
    hearts:   { run: fxHearts,   delay: 700 },
    sparkles: { run: fxSparkles, delay: 500 },
    petals:   { run: fxPetals,   delay: 700 },
    emoji:    { run: fxEmoji,    delay: 600 },
    rain:     { run: fxRain,     delay: 900 },
    ripple:   { run: fxRipple,   delay: 350 },
    pulse:    { run: fxPulse,    delay: 350 },
    squish:   { run: fxSquish,   delay: 300 },
    shimmer:  { run: fxShimmer,  delay: 450 },
    success:  { run: fxSuccess,  delay: 900 },
  };

  /* ── magnetic hover: [data-ps-magnetic] elements lean toward the cursor
     and spring back on leave (reduced-motion off) ── */
  function wireMagnetic() {
    if (reduced.matches) return;
    var els = document.querySelectorAll('[data-ps-magnetic]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.__psMag) return;
      el.__psMag = true;
      el.style.transition = 'translate 0.18s cubic-bezier(0.22, 1, 0.36, 1)';
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        var dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        el.style.translate = (dx * 7) + 'px ' + (dy * 5) + 'px';
      });
      el.addEventListener('pointerleave', function () { el.style.translate = '0px 0px'; });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireMagnetic);
  else wireMagnetic();

  /* tiny hook for sibling layers (ps-widgets fires the count-up celebration
     through the same engine so "confetti" is identical to the button effect) */
  window.__psMotionFire = function (name, el) {
    var spec = FX[name];
    if (spec && el && !reduced.matches) spec.run(el);
  };

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      var el = t && t.closest ? t.closest('[data-ps-effect], [data-ps-confetti]') : null;
      if (!el || reduced.matches) return;
      var spec = FX[el.getAttribute('data-ps-effect') || 'confetti'];
      if (!spec) return;
      spec.run(el, e);
      /* real same-tab navigation would cut the effect short — let it play,
         then go. Anchors, new tabs, and modified clicks navigate as-is. */
      var href = el.getAttribute('href');
      var target = el.getAttribute('target') || '_self';
      if (
        href &&
        href.charAt(0) !== '#' &&
        target === '_self' &&
        !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
      ) {
        e.preventDefault();
        setTimeout(function () { window.location.href = href; }, spec.delay);
      } else if (!href && el.form && (el.type === 'submit' || el.tagName === 'BUTTON')) {
        /* submit buttons (ps-product add-to-cart): hold the form just long
           enough for the effect to land, then submit for real */
        e.preventDefault();
        var form = el.form;
        setTimeout(function () {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }, spec.delay);
      }
    },
    true
  );

  /* ─────────────────────── Scroll scenes ──────────────────────── */
  /* ps-scrollscene renders scenes stacked in normal flow (the no-JS and
     editor-canvas state). When this script runs — and motion is OK — it
     flips the section into pinned mode via [data-ps-motion] and drives
     the active scene + a 0..1 --ps-progress var from scroll position. */

  function initScenes() {
    if (reduced.matches) return;
    var secs = document.querySelectorAll('[data-ps-scrollscene]');
    if (!secs.length) return;
    var tracked = [];
    for (var i = 0; i < secs.length; i++) {
      var sec = secs[i];
      var scenes = sec.querySelectorAll('[data-ps-scene]');
      if (scenes.length < 2) continue; /* one scene = nothing to scrub */
      sec.setAttribute('data-ps-motion', '');
      sec.setAttribute('data-ps-active', '0');
      tracked.push({ el: sec, n: scenes.length, active: 0 });
    }
    if (!tracked.length) return;

    var pending = 0;
    function update() {
      pending = 0;
      var vh = window.innerHeight;
      for (var i = 0; i < tracked.length; i++) {
        var s = tracked[i];
        var rect = s.el.getBoundingClientRect();
        if (rect.bottom < -vh || rect.top > vh * 2) continue; /* far offscreen */
        var total = rect.height - vh;
        var p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
        var idx = Math.min(s.n - 1, Math.floor(p * s.n));
        if (idx !== s.active) {
          s.active = idx;
          s.el.setAttribute('data-ps-active', String(idx));
        }
        s.el.style.setProperty('--ps-progress', p.toFixed(4));
      }
    }
    function onScroll() {
      if (!pending) pending = requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScenes);
  } else {
    initScenes();
  }
})();
